import { Component, type ReactNode, type ErrorInfo } from "react";
import styled from "styled-components";
import { Typography } from "../Typography";
import { Button } from "../Button";

export interface ErrorBoundaryProps {
	/** Content to render when no error */
	children: ReactNode;
	/** Custom fallback UI to render on error */
	fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo, reset: () => void) => ReactNode);
	/** Callback when error is caught */
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
	/** Whether to log errors to console */
	logErrors?: boolean;
	/** Custom error message to display */
	errorMessage?: string;
	/** Whether to show error details in fallback UI */
	showErrorDetails?: boolean;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component for React 19 that catches JavaScript errors in child components,
 * logs them, and displays a fallback UI.
 *
 * @component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components to wrap
 * @param {ReactNode|Function} [props.fallback] - Custom fallback UI or render function
 * @param {Function} [props.onError] - Callback function called when error is caught
 * @param {boolean} [props.logErrors=true] - Whether to log errors to console
 * @param {string} [props.errorMessage="Something went wrong"] - Custom error message
 * @param {boolean} [props.showErrorDetails=false] - Whether to show technical error details
 *
 * @example
 * ```tsx
 * <ErrorBoundary
 *   onError={(error, errorInfo) => logToService(error, errorInfo)}
 *   errorMessage="Oops! The app encountered an error"
 * >
 *   <App />
 * </ErrorBoundary>
 * ```
 *
 * @example
 * ```tsx
 * <ErrorBoundary
 *   fallback={(error, errorInfo, reset) => (
 *     <div>
 *       <h1>Custom Error UI</h1>
 *       <button onClick={reset}>Try Again</button>
 *     </div>
 *   )}
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
		};
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return {
			hasError: true,
			error,
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		const { onError, logErrors = true } = this.props;

		// Log error to console if enabled
		if (logErrors) {
			console.error("ErrorBoundary caught an error:", error, errorInfo);
		}

		// Update state with error info
		this.setState({
			errorInfo,
		});

		// Call custom error handler if provided
		if (onError) {
			onError(error, errorInfo);
		}
	}

	resetErrorBoundary = (): void => {
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null,
		});
	};

	render(): ReactNode {
		const { hasError, error, errorInfo } = this.state;
		const {
			children,
			fallback,
			errorMessage = "Something went wrong",
			showErrorDetails = false,
		} = this.props;

		if (hasError && error) {
			// Render custom fallback if provided
			if (fallback) {
				if (typeof fallback === "function") {
					return fallback(error, errorInfo!, this.resetErrorBoundary);
				}
				return fallback;
			}

			// Render default fallback UI
			return (
				<ErrorContainer>
					<ErrorContent>
						<Typography variant="h2" style={{ marginBottom: "1rem" }}>
							{errorMessage}
						</Typography>
						<Typography variant="p" style={{ marginBottom: "2rem", opacity: 0.7 }}>
							The application encountered an unexpected error. Please try refreshing the page.
						</Typography>
						{showErrorDetails && (
							<ErrorDetails>
								<Typography variant="h4" style={{ marginBottom: "0.5rem" }}>
									Error Details:
								</Typography>
								<ErrorMessage>
									<Typography variant="code">{error.toString()}</Typography>
								</ErrorMessage>
								{errorInfo?.componentStack && (
									<ComponentStack>
										<Typography variant="small">{errorInfo.componentStack}</Typography>
									</ComponentStack>
								)}
							</ErrorDetails>
						)}
						<Button
							label="Try Again"
							onClick={this.resetErrorBoundary}
							variant="primary"
							size="md"
						/>
					</ErrorContent>
				</ErrorContainer>
			);
		}

		return children;
	}
}

const ErrorContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 400px;
	padding: 2rem;
	background-color: red;
`;

const ErrorContent = styled.div`
	max-width: 600px;
	text-align: center;
`;

const ErrorDetails = styled.div`
	background-color: rgba(255, 0, 0, 0.05);
	border: 1px solid rgba(255, 0, 0, 0.2);
	border-radius: 8px;
	padding: 1rem;
	margin-bottom: 2rem;
	text-align: left;
`;

const ErrorMessage = styled.pre`
	background-color: rgba(0, 0, 0, 0.05);
	padding: 0.5rem;
	border-radius: 4px;
	overflow-x: auto;
	margin: 0.5rem 0;
	white-space: pre-wrap;
	word-break: break-word;
`;

const ComponentStack = styled.pre`
	background-color: rgba(0, 0, 0, 0.05);
	padding: 0.5rem;
	border-radius: 4px;
	overflow-x: auto;
	margin-top: 0.5rem;
	font-size: 0.75rem;
	white-space: pre-wrap;
	word-break: break-word;
	max-height: 200px;
	overflow-y: auto;
`;
